import { LayoutGrid, Square } from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatDateTime } from '@/lib/datetime'
import { Card } from '@/components/ui/Card'
import styles from './AreaPricingPage.module.css'

interface PricingModeCardProps {
  /** True when Cairo is priced by sub-area; false when it is one area at one price. */
  enabled: boolean
  updatedAt: string | null
  updatedByAdminName: string | null
  disabled: boolean
  /** Asks to change the mode. Nothing changes until the caller has confirmed it. */
  onChoose: (enabled: boolean) => void
}

/**
 * The one choice on this page customers feel: is Cairo one area at one price,
 * or priced by sub-area?
 *
 * The radios always show what the server says. Picking the other one asks the
 * page to confirm the change, and only a confirmed change moves them — so a
 * stray click, or a cancelled dialog, never looks like a change that was made.
 *
 * "One area" is the setting Cairo was always priced by, and the one every new
 * installation starts in. Everything drawn below is kept in either mode.
 */
export function PricingModeCard({ enabled, updatedAt, updatedByAdminName, disabled, onChoose }: PricingModeCardProps) {
  return (
    <Card className={styles.modeCard}>
      <fieldset className={styles.modeFieldset} disabled={disabled}>
        <legend className={styles.modeLegend}>How Cairo is priced</legend>
        <div className={styles.modeOptions}>
          <label className={cn(styles.modeOption, !enabled && styles.modeOptionChosen)}>
            <input type="radio" name="cairo-pricing" checked={!enabled} onChange={() => onChoose(false)} />
            <Square aria-hidden="true" className={styles.modeIcon} />
            <span className={styles.modeText}>
              <strong>One area</strong>
              <span>
                Every Cairo trip costs its package price, wherever it starts and ends. The sub-areas and groups
                below are kept, but ignored.
              </span>
            </span>
          </label>
          <label className={cn(styles.modeOption, enabled && styles.modeOptionChosen)}>
            <input type="radio" name="cairo-pricing" checked={enabled} onChange={() => onChoose(true)} />
            <LayoutGrid aria-hidden="true" className={styles.modeIcon} />
            <span className={styles.modeText}>
              <strong>Priced by sub-area</strong>
              <span>
                Each end of a Cairo trip adds its group&apos;s surcharge, as listed under “What a trip adds”.
                Customers see one price per package.
              </span>
            </span>
          </label>
        </div>
        {updatedAt ? (
          <p className={styles.modeChanged}>
            Set to {enabled ? 'priced by sub-area' : 'one area'} {formatDateTime(updatedAt)}
            {updatedByAdminName ? ` by ${updatedByAdminName}` : ''}.
          </p>
        ) : null}
      </fieldset>
    </Card>
  )
}
